import { useEffect, useRef } from "react";

interface City {
    name: string;
    lat: number;
    lng: number;
    flag: string;
    currency: string;
}

// Major financial / payments hubs (mix of Africa + global)
const CITIES: City[] = [
    { name: "Lagos", lat: 6.5, lng: 3.4, flag: "/images/NG.svg", currency: "NGN" },
    { name: "Accra", lat: 5.6, lng: -0.2, flag: "/images/GH.svg", currency: "GHS" },
    { name: "Nairobi", lat: -1.3, lng: 36.8, flag: "/images/KE.svg", currency: "KES" },
    { name: "Johannesburg", lat: -26.2, lng: 28.0, flag: "/images/ZA.svg", currency: "ZAR" },
    { name: "Cairo", lat: 30.0, lng: 31.2, flag: "/images/EG.svg", currency: "EGP" },
    { name: "Casablanca", lat: 33.6, lng: -7.6, flag: "/images/MA.svg", currency: "MAD" },
    { name: "London", lat: 51.5, lng: -0.1, flag: "/images/GB.svg", currency: "GBP" },
    { name: "Frankfurt", lat: 50.1, lng: 8.7, flag: "/images/DE.svg", currency: "EUR" },
    { name: "New York", lat: 40.7, lng: -74.0, flag: "/images/US.svg", currency: "USD" },
    { name: "Toronto", lat: 43.7, lng: -79.4, flag: "/images/CA.svg", currency: "CAD" },
    { name: "Dubai", lat: 25.3, lng: 55.3, flag: "/images/AE.svg", currency: "AED" },
    { name: "Mumbai", lat: 19.1, lng: 72.9, flag: "/images/IN.svg", currency: "INR" },
    { name: "Singapore", lat: 1.4, lng: 103.8, flag: "/images/SG.svg", currency: "SGD" },
    { name: "Hong Kong", lat: 22.3, lng: 114.2, flag: "/images/HK.svg", currency: "HKD" },
    { name: "Tokyo", lat: 35.7, lng: 139.7, flag: "/images/JP.svg", currency: "JPY" },
    { name: "Sydney", lat: -33.9, lng: 151.2, flag: "/images/AU.svg", currency: "AUD" },
];

// Popup lifecycle (ms)
const POPUP_LIFETIME = 2600;
const POPUP_FADE_IN = 220;
const POPUP_FADE_OUT = 380;
const POPUP_SPAWN_INTERVAL = 1500;
const MAX_POPUPS = 2;

interface Popup {
    cityIdx: number;
    bornAt: number;
}

const MERIDIAN_COUNT = 12; // longitude lines
const PARALLEL_COUNT = 7; // latitude lines (excluding poles)
const POINTS_PER_LINE = 64;
const ROTATION_SPEED = 0.0035; // radians per frame at 60fps
const AXIS_TILT = (23.5 * Math.PI) / 180; // earth-like axial tilt
const GLOBE_COLOR = "0, 201, 98"; // brand green
const CITY_COLOR = "255, 255, 255";

const latLngToVec3 = (latDeg: number, lngDeg: number) => {
    const lat = (latDeg * Math.PI) / 180;
    const lng = (lngDeg * Math.PI) / 180;
    return {
        x: Math.cos(lat) * Math.sin(lng),
        y: Math.sin(lat),
        z: Math.cos(lat) * Math.cos(lng),
    };
};

const RotatingGlobe = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        let rafId = 0;
        let theta = 0;
        let last = performance.now();
        let lastSpawn = 0;
        const popups: Popup[] = [];

        // Preload flag images
        const flagImages: (HTMLImageElement | null)[] = CITIES.map((c) => {
            const img = new Image();
            img.src = c.flag;
            img.onerror = () => {
                const idx = CITIES.findIndex((x) => x.flag === c.flag);
                if (idx >= 0) flagImages[idx] = null;
            };
            return img;
        });

        // Pre-compute meridian / parallel point lists in unit-sphere coords
        const meridians: { x: number; y: number; z: number }[][] = [];
        for (let m = 0; m < MERIDIAN_COUNT; m++) {
            const lng = (m / MERIDIAN_COUNT) * 360 - 180;
            const line: { x: number; y: number; z: number }[] = [];
            for (let i = 0; i <= POINTS_PER_LINE; i++) {
                const lat = (i / POINTS_PER_LINE) * 180 - 90;
                line.push(latLngToVec3(lat, lng));
            }
            meridians.push(line);
        }

        const parallels: { x: number; y: number; z: number }[][] = [];
        for (let p = 1; p <= PARALLEL_COUNT; p++) {
            const lat = (p / (PARALLEL_COUNT + 1)) * 180 - 90;
            const line: { x: number; y: number; z: number }[] = [];
            for (let i = 0; i <= POINTS_PER_LINE; i++) {
                const lng = (i / POINTS_PER_LINE) * 360 - 180;
                line.push(latLngToVec3(lat, lng));
            }
            parallels.push(line);
        }

        const cityVecs = CITIES.map((c) => latLngToVec3(c.lat, c.lng));

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;
            canvas.width = Math.floor(rect.width * dpr);
            canvas.height = Math.floor(rect.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();

        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        // Apply rotation around Y, then tilt around X
        const cosTilt = Math.cos(AXIS_TILT);
        const sinTilt = Math.sin(AXIS_TILT);

        const transform = (
            v: { x: number; y: number; z: number },
            cosT: number,
            sinT: number
        ) => {
            // Rotate around Y axis
            const x1 = cosT * v.x + sinT * v.z;
            const z1 = -sinT * v.x + cosT * v.z;
            const y1 = v.y;
            // Tilt around X axis
            const y2 = cosTilt * y1 - sinTilt * z1;
            const z2 = sinTilt * y1 + cosTilt * z1;
            return { x: x1, y: y2, z: z2 };
        };

        const tick = (now: number) => {
            const dt = Math.min(now - last, 64);
            last = now;
            if (!reduceMotion) {
                theta += ROTATION_SPEED * (dt / 16);
            }

            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            ctx.clearRect(0, 0, w, h);

            const cx = w / 2;
            const cy = h / 2;
            const radius = Math.min(w, h) * 0.42;

            const cosT = Math.cos(theta);
            const sinT = Math.sin(theta);

            // Outer atmospheric glow
            const outerGlow = ctx.createRadialGradient(
                cx,
                cy,
                radius * 0.96,
                cx,
                cy,
                radius * 1.25
            );
            outerGlow.addColorStop(0, `rgba(${GLOBE_COLOR}, 0.22)`);
            outerGlow.addColorStop(1, `rgba(${GLOBE_COLOR}, 0)`);
            ctx.fillStyle = outerGlow;
            ctx.beginPath();
            ctx.arc(cx, cy, radius * 1.25, 0, Math.PI * 2);
            ctx.fill();

            // Inner sphere fill (subtle dark gradient for depth)
            const sphereFill = ctx.createRadialGradient(
                cx - radius * 0.3,
                cy - radius * 0.3,
                radius * 0.1,
                cx,
                cy,
                radius
            );
            sphereFill.addColorStop(0, "rgba(15, 81, 50, 0.55)");
            sphereFill.addColorStop(1, "rgba(8, 32, 22, 0.85)");
            ctx.fillStyle = sphereFill;
            ctx.beginPath();
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
            ctx.fill();

            // Helper: draw a polyline with per-segment alpha based on z
            const drawLine = (
                points: { x: number; y: number; z: number }[],
                baseAlpha: number,
                lineWidth: number
            ) => {
                let prevX = 0;
                let prevY = 0;
                let prevZ = -2;
                for (let i = 0; i < points.length; i++) {
                    const p = transform(points[i], cosT, sinT);
                    const sx = cx + p.x * radius;
                    const sy = cy - p.y * radius;
                    if (i > 0) {
                        const avgZ = (p.z + prevZ) / 2;
                        // Map z (-1 back .. 1 front) to alpha falloff
                        const zAlpha = Math.max(0, (avgZ + 0.4) / 1.4);
                        const a = baseAlpha * zAlpha;
                        if (a > 0.01) {
                            ctx.strokeStyle = `rgba(${GLOBE_COLOR}, ${a})`;
                            ctx.lineWidth = lineWidth;
                            ctx.beginPath();
                            ctx.moveTo(prevX, prevY);
                            ctx.lineTo(sx, sy);
                            ctx.stroke();
                        }
                    }
                    prevX = sx;
                    prevY = sy;
                    prevZ = p.z;
                }
            };

            // Equator a bit brighter than other parallels
            for (let i = 0; i < parallels.length; i++) {
                const isEquator = i === Math.floor(PARALLEL_COUNT / 2);
                drawLine(parallels[i], isEquator ? 0.55 : 0.32, isEquator ? 0.9 : 0.6);
            }
            for (const m of meridians) {
                drawLine(m, 0.32, 0.6);
            }

            // Outer rim ring
            ctx.strokeStyle = `rgba(${GLOBE_COLOR}, 0.5)`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
            ctx.stroke();

            // Compute on-screen city positions once (used for dots + popups)
            const cityScreen: ({
                sx: number;
                sy: number;
                z: number;
                visibility: number;
            } | null)[] = new Array(cityVecs.length);

            const pulsePhase = (now / 1200) % (Math.PI * 2);
            for (let i = 0; i < cityVecs.length; i++) {
                const p = transform(cityVecs[i], cosT, sinT);
                if (p.z < -0.05) {
                    cityScreen[i] = null;
                    continue;
                }
                const sx = cx + p.x * radius;
                const sy = cy - p.y * radius;
                const visibility = Math.min(1, (p.z + 0.05) / 0.5);
                cityScreen[i] = { sx, sy, z: p.z, visibility };

                const phase = pulsePhase + i * 0.6;
                const pulse = 0.5 + 0.5 * Math.sin(phase);

                const haloR = 5 + pulse * 6;
                const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, haloR);
                halo.addColorStop(
                    0,
                    `rgba(${GLOBE_COLOR}, ${0.55 * visibility})`
                );
                halo.addColorStop(1, `rgba(${GLOBE_COLOR}, 0)`);
                ctx.fillStyle = halo;
                ctx.beginPath();
                ctx.arc(sx, sy, haloR, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = `rgba(${CITY_COLOR}, ${0.95 * visibility})`;
                ctx.beginPath();
                ctx.arc(sx, sy, 1.8, 0, Math.PI * 2);
                ctx.fill();
            }

            // ----- Popup scheduler -----
            if (
                !reduceMotion &&
                popups.length < MAX_POPUPS &&
                now - lastSpawn > POPUP_SPAWN_INTERVAL
            ) {
                // Pick a visible city not already shown
                const activeIdxs = new Set(popups.map((p) => p.cityIdx));
                const candidates: number[] = [];
                for (let i = 0; i < cityScreen.length; i++) {
                    if (
                        cityScreen[i] &&
                        cityScreen[i]!.z > 0.25 &&
                        !activeIdxs.has(i) &&
                        flagImages[i] &&
                        flagImages[i]!.complete &&
                        flagImages[i]!.naturalWidth > 0
                    ) {
                        candidates.push(i);
                    }
                }
                if (candidates.length > 0) {
                    const pick =
                        candidates[
                            Math.floor(Math.random() * candidates.length)
                        ];
                    popups.push({ cityIdx: pick, bornAt: now });
                    lastSpawn = now;
                }
            }

            // ----- Render popups -----
            for (let i = popups.length - 1; i >= 0; i--) {
                const pp = popups[i];
                const age = now - pp.bornAt;
                if (age > POPUP_LIFETIME) {
                    popups.splice(i, 1);
                    continue;
                }

                const screen = cityScreen[pp.cityIdx];
                // Hide if city rotated out of view
                if (!screen || screen.z < 0.05) continue;

                // Fade in / out alpha
                let alpha: number;
                if (age < POPUP_FADE_IN) {
                    alpha = age / POPUP_FADE_IN;
                } else if (age > POPUP_LIFETIME - POPUP_FADE_OUT) {
                    alpha =
                        (POPUP_LIFETIME - age) / POPUP_FADE_OUT;
                } else {
                    alpha = 1;
                }
                alpha = Math.max(0, Math.min(1, alpha));

                // Slight pop-in scale
                const scale =
                    age < POPUP_FADE_IN
                        ? 0.7 + 0.3 * (age / POPUP_FADE_IN)
                        : 1;

                const city = CITIES[pp.cityIdx];
                const flagImg = flagImages[pp.cityIdx];

                // Card geometry
                const flagW = 22 * scale;
                const flagH = 14 * scale;
                ctx.font =
                    '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
                const textW = ctx.measureText(city.currency).width;
                const padX = 8 * scale;
                const gap = 6 * scale;
                const cardH = 22 * scale;
                const cardW = padX * 2 + flagW + gap + textW;

                // Anchor the card above the dot, with a connector
                const dotX = screen.sx;
                const dotY = screen.sy;
                const offsetY = 22;
                const cardX = dotX - cardW / 2;
                const cardY = dotY - offsetY - cardH;

                // Connector line
                ctx.strokeStyle = `rgba(255, 255, 255, ${0.45 * alpha})`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(dotX, dotY - 3);
                ctx.lineTo(dotX, cardY + cardH);
                ctx.stroke();

                // Card background
                ctx.fillStyle = `rgba(8, 32, 22, ${0.92 * alpha})`;
                ctx.strokeStyle = `rgba(${GLOBE_COLOR}, ${0.55 * alpha})`;
                ctx.lineWidth = 1;
                const radiusCorner = 6 * scale;
                roundRect(
                    ctx,
                    cardX,
                    cardY,
                    cardW,
                    cardH,
                    radiusCorner
                );
                ctx.fill();
                ctx.stroke();

                // Flag (if image loaded)
                if (flagImg && flagImg.complete && flagImg.naturalWidth > 0) {
                    ctx.save();
                    ctx.globalAlpha = alpha;
                    ctx.drawImage(
                        flagImg,
                        cardX + padX,
                        cardY + (cardH - flagH) / 2,
                        flagW,
                        flagH
                    );
                    ctx.restore();
                }

                // Currency label
                ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
                ctx.textAlign = "left";
                ctx.textBaseline = "middle";
                ctx.fillText(
                    city.currency,
                    cardX + padX + flagW + gap,
                    cardY + cardH / 2 + 0.5
                );
            }

            rafId = requestAnimationFrame(tick);
        };

        // Helper: rounded rect path
        function roundRect(
            c: CanvasRenderingContext2D,
            x: number,
            y: number,
            w: number,
            h: number,
            r: number
        ) {
            const rr = Math.min(r, w / 2, h / 2);
            c.beginPath();
            c.moveTo(x + rr, y);
            c.lineTo(x + w - rr, y);
            c.quadraticCurveTo(x + w, y, x + w, y + rr);
            c.lineTo(x + w, y + h - rr);
            c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
            c.lineTo(x + rr, y + h);
            c.quadraticCurveTo(x, y + h, x, y + h - rr);
            c.lineTo(x, y + rr);
            c.quadraticCurveTo(x, y, x + rr, y);
            c.closePath();
        }

        rafId = requestAnimationFrame(tick);

        const handleVisibility = () => {
            if (document.hidden) {
                cancelAnimationFrame(rafId);
            } else {
                last = performance.now();
                rafId = requestAnimationFrame(tick);
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="w-full h-full pointer-events-none"
            aria-hidden="true"
        />
    );
};

export default RotatingGlobe;
